import Deal from '@r6digital/rapidstor-types/types/Deal';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

/**
 * Finds a matching deal on a unit type based on deal ID
 * @param unitType - The unit type to search for deals
 * @param dealId - The deal ID to match
 * @returns The matching deal or null if not found
 */
export function findMatchingDeal(
    unitType: UnitType | undefined,
    dealId: string | undefined,
): Deal | null {
    if (!dealId || !unitType?.deals || unitType.deals.length === 0) {
        return null;
    }

    const matchingDeal = unitType.deals.find((deal) => deal._id === dealId);
    return matchingDeal || null;
}

/**
 * Returns the display rate for a deal. When the discount plan has 100% off
 * (free month), discountedRate is 0 which is misleading — return rate instead.
 */
export function getEffectiveDealRate(deal: Deal, rate: number): number {
    if (deal.discountPlan?.dcPCDiscount === 100 || (deal.rates.discounted && deal.rates.discountedRate === 0)) {
        return rate;
    }

    if (deal.discountPlan?.dcChgAmt) {
        return rate;
    }

    return deal.rates.discountedRate ?? rate;
}

/**
 * Gets the appropriate rate for a unit type based on whether a matching deal exists.
 * When a matching deal exists, uses the deal's discountedRate.
 * When no matching deal but the unit has its own deal(s), uses rates.discountedRate
 * (the price the user sees in the listing) rather than rate.
 * When no deals at all, falls back to rate (the true standard rate).
 * @param unitType - The unit type to get rate from
 * @param dealId - The deal ID to check for
 * @returns The matching deal's discountedRate, the unit's own applied deal rate, or rate
 */
export function getUnitTypeRate(
    unitType: UnitType | undefined,
    dealId: string | undefined,
): number {
    if (!unitType) {
        return 0;
    }

    const matchingDeal = findMatchingDeal(unitType, dealId);
    if (matchingDeal) {
        return getEffectiveDealRate(matchingDeal, unitType.rates.rate);
    }

    // No matching deal — if the unit has its own deal(s) applied, use that displayed rate
    // (rates.discountedRate reflects what the user sees in the listing/slider)
    if (unitType.deals && unitType.deals.length > 0 && unitType.rates.discountedRate != null) {
        return unitType.rates.discountedRate;
    }

    return unitType.rates.rate;
}

/**
 * Attempts to match a deal on a new unit type when switching units.
 * Used to determine if the current deal carries over to a different unit.
 * @param newUnitType - The unit type being switched to
 * @param currentDealId - The currently selected deal ID
 * @returns Object with the resolved deal info and whether a match was found
 */
export function resolveDealForUnitSwitch(
    newUnitType: UnitType,
    currentDealId: string | null,
): { deal: string | null; dealObject: Deal | null; matched: boolean } {
    if (!currentDealId) {
        return { deal: null, dealObject: null, matched: false };
    }

    const matchingDeal = findMatchingDeal(newUnitType, currentDealId);
    if (matchingDeal) {
        return { deal: matchingDeal._id as string, dealObject: matchingDeal, matched: true };
    }

    return { deal: null, dealObject: null, matched: false };
}
